import {
	Children,
	PropsWithChildren,
	ReactElement,
	cloneElement,
	isValidElement,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ChevronRightIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Callout, FlexGap, Reorder, Skeleton, tokens } from '@treams/treams-ui';

import GoalCard from './GoalCard';

const Expand = styled(Flex)<{ expanded?: boolean }>`
	flex-shrink: 0;
	align-items: center;
	padding: 0 ${tokens.padding.compact}px;
	border-radius: ${tokens.radius.sm}px;
	cursor: pointer;
	color: ${tokens.icon.secondary};

	svg {
		display: block;
		width: 16px;
		height: 16px;
		transition: 0.2s ease transform;
		${(props) => props.expanded && 'transform: rotateZ(90deg);'}

		path {
			fill: currentColor;
		}
	}

	&:hover {
		background: ${tokens.surface.hover};
		color: ${tokens.icon.primary};
	}
`;

function GoalTreeChildren(props: {
	goal: graphql.Goal;
	user?: graphql.User;
	small?: boolean;
	inline?: boolean;
}) {
	const { t } = useTranslation();
	const query = graphql.useGoalChildrenQuery({ variables: { id: props.goal.id } });

	const recursive = query.data?.goal.children.edges.some(
		({ node }) => (node.children.totalCount ?? 0) > 0,
	);

	const ml = props.inline && !recursive ? 0 : tokens.gap.section;

	return (
		<Box ml={ml} mt={`${tokens.gap.list}px`}>
			{query.data ? (
				<FlexGap flexDirection="column" gap={tokens.gap.list}>
					{query.data.goal.children.edges.map(({ node }) => (
						<GoalTree
							key={node.id}
							goal={node as graphql.Goal}
							user={props.user}
							small={props.small}
						>
							<GoalCard goal={node as graphql.Goal} user={props.user} small={props.small} />
						</GoalTree>
					))}
					{props.goal.children.totalCount &&
						query.data.goal.children.edges.length !== props.goal.children.totalCount && (
							<Callout
								mt={2}
								color="blue"
								title={t('goals.chilren.private.title')}
								description={t('goals.chilren.private.description', {
									count: props.goal.children.totalCount - query.data.goal.children.edges.length,
								})}
							/>
						)}
				</FlexGap>
			) : (
				<Skeleton.Repeat count={props.goal.children.totalCount ?? 0}>
					<Box>
						<GoalCard.Skeleton />
					</Box>
				</Skeleton.Repeat>
			)}
		</Box>
	);
}

export default function GoalTree(
	props: PropsWithChildren<{ goal: graphql.Goal; user?: graphql.User; small?: boolean } & BoxProps>,
) {
	const { goal, user, small, ...rest } = props;

	const [expanded, setExpanded] = useState(false);

	if (!props.goal.viewerCanViewChildren || (props.goal.children.totalCount ?? 0) === 0) {
		return <Box {...(rest as any)}>{props.children}</Box>;
	}

	return (
		<>
			<Flex alignItems="stretch" {...(rest as any)}>
				<Expand
					flexGrow={0}
					flexShrink={0}
					alignItems="center"
					expanded={expanded}
					onClick={() => setExpanded(!expanded)}
				>
					<ChevronRightIcon />
				</Expand>
				<Box flexGrow={1} flexShrink={1} minWidth={0}>
					{props.children}
				</Box>
			</Flex>
			{expanded && <GoalTreeChildren goal={goal} user={user} small={small} />}
		</>
	);
}

GoalTree.Reorder = function GoalTreeReorder(
	props: PropsWithChildren<
		{
			index: number;
			loading: boolean;
			goal: graphql.Goal;
			user?: graphql.User;
		} & BoxProps
	>,
) {
	const { goal, user, index, loading, ...rest } = props;

	const [expanded, setExpanded] = useState(false);
	const hasChildren = goal.viewerCanViewChildren && (goal.children.totalCount ?? 0) > 0;

	return (
		<Reorder
			{...rest}
			id={goal.id}
			index={index}
			loading={loading}
			render={({ dragHandleProps, isDragging }) => (
				<>
					<Flex alignItems="stretch">
						{hasChildren && (
							<Expand expanded={expanded} onClick={() => setExpanded((value) => !value)}>
								<ChevronRightIcon />
							</Expand>
						)}
						<Box flexGrow={1} flexShrink={1} minWidth={0}>
							{Children.map(props.children, (child) =>
								isValidElement(child)
									? cloneElement(
											child as ReactElement<{
												draggable?: boolean;
												dragHandleProps?: typeof dragHandleProps;
												isDragging?: boolean;
												loading?: boolean;
											}>,
											{
												draggable: true,
												dragHandleProps: dragHandleProps ?? undefined,
												isDragging,
												loading,
											},
										)
									: child,
							)}
						</Box>
					</Flex>
					{expanded && <GoalTreeChildren goal={goal} user={user} small />}
				</>
			)}
		/>
	);
};
